export type TableCell = unknown;
export type TableRow = Record<string | number, TableCell>;
export type TableColumn = {
  header: string;
  key: string | number;
  /** Optional value-type hint (e.g. 'date') consumed by AuiTable.format. When
   *  omitted the type is auto-detected from the column's values. */
  type?: string;
};
export type SortState = { key: string | number; dir: 'asc' | 'desc' } | null;
export type ValueFilters = Record<string, Set<string>>;

// Cells render through markdown; leading `-`, `+`, `*`, `#`, `>`, or `1.` get
// parsed as list/heading/blockquote markers and the literal character disappears
// (e.g. "- $5.00" becomes a bulleted "$5.00"). Escape them at the start of the
// cell so they render as text. Inline emphasis (`**bold**`, `*italic*`, links)
// is unaffected because those don't start with marker+space.
export function escapeLeadingBlockMarkdown(s: string): string {
  return s
    .replace(/^(\s*)([-+*#>])(\s)/, '$1\\$2$3')
    .replace(/^(\s*)(\d+)([.)])(\s)/, '$1$2\\$3$4');
}

export function coerceCellToString(cell: TableCell): string {
  if (cell == null) return '';
  if (typeof cell === 'object') {
    const obj = cell as Record<string, unknown>;
    if ('sparkline' in obj) {
      try {
        return JSON.stringify(obj.sparkline);
      } catch {
        return '';
      }
    }
    try {
      return JSON.stringify(cell);
    } catch {
      return '';
    }
  }
  return String(cell);
}

export function getDistinctValues(rows: TableRow[], key: string | number): string[] {
  const set = new Set<string>();
  for (const row of rows) set.add(coerceCellToString(row[key]));
  return Array.from(set).sort((a, b) => {
    const aEmpty = a === '';
    const bEmpty = b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });
}

export function filterRows(rows: TableRow[], filters: ValueFilters): TableRow[] {
  const active = Object.entries(filters).filter(([, set]) => set && set.size > 0);
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every(([key, excluded]) => !excluded.has(coerceCellToString(row[key]))),
  );
}

// Recognises currency-prefixed and percent-suffixed numbers with thousand
// separators: "$5,200.00", "-€42.5", "- $1,000.00", "+ $420.00", "100%",
// "1,234". The sign and currency may be in either order, with optional
// whitespace between them. Returns null when the cell isn't an unambiguous
// number, so we can fall back to locale compare.
const NUMERIC_LIKE =
  /^\s*[-+]?\s*[$€£¥]?\s*[-+]?\s*\d(?:[\d,]*\d)?(?:\.\d+)?\s*%?\s*$/;

export function tryParseNumber(s: string): number | null {
  if (!NUMERIC_LIKE.test(s)) return null;
  const cleaned = s.replace(/[$€£¥%\s]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function compareNonEmpty(a: TableCell, b: TableCell): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  const as = coerceCellToString(a);
  const bs = coerceCellToString(b);
  const an = tryParseNumber(as);
  const bn = tryParseNumber(bs);
  if (an !== null && bn !== null) {
    return an < bn ? -1 : an > bn ? 1 : 0;
  }
  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows(rows: TableRow[], sort: SortState): TableRow[] {
  if (!sort) return rows;
  const { key, dir } = sort;
  const dirMul = dir === 'asc' ? 1 : -1;
  const indexed = rows.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => {
    const av = a.r[key];
    const bv = b.r[key];
    const aEmpty = av == null || av === '';
    const bEmpty = bv == null || bv === '';
    if (aEmpty && bEmpty) return a.i - b.i;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp = compareNonEmpty(av, bv) * dirMul;
    return cmp !== 0 ? cmp : a.i - b.i;
  });
  return indexed.map((x) => x.r);
}

function csvEscape(cell: string): string {
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}

export function rowsToCsv(rows: TableRow[], columns: TableColumn[]): string {
  const header = columns.map((c) => csvEscape(c.header)).join(',');
  const body = rows.map((row) =>
    columns.map((c) => csvEscape(coerceCellToString(row[c.key]))).join(','),
  );
  return [header, ...body].join('\r\n');
}

/**
 * Parse CSV text into row objects keyed by the header row. The inverse of
 * `rowsToCsv`: quote-aware (handles embedded commas, `""` escapes, and
 * newlines inside quoted fields) and tolerant of both `\r\n` and `\n` line
 * endings plus a trailing newline. Returns `[]` for empty/header-only input.
 *
 * Cell values are left as strings — the table's display/sort path already
 * coerces numbers (`tryParseNumber`), so we don't second-guess types here.
 */
export function csvToRows(text: string): TableRow[] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  let sawAny = false;

  const endField = () => {
    record.push(field);
    field = '';
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    sawAny = true;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      endField();
    } else if (ch === '\n') {
      endRecord();
    } else if (ch === '\r') {
      // Swallow CR; the following LF (if any) ends the record.
      if (text[i + 1] !== '\n') endRecord();
    } else {
      field += ch;
    }
  }
  // Flush the final field/record unless the input ended exactly on a newline.
  if (sawAny && (field !== '' || record.length > 0)) endRecord();

  if (records.length < 2) return [];
  const headers = records[0];
  return records.slice(1).map((cells) => {
    const row: TableRow = {};
    headers.forEach((h, idx) => {
      row[h] = cells[idx] ?? '';
    });
    return row;
  });
}

/** Does this `rowsUrl` (ignoring any query string) point at a `.csv`? */
function isCsvUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return /\.csv$/i.test(path);
}

/**
 * Parse fetched `rowsUrl` content into table rows. CSV URLs (`.csv`) parse
 * as CSV; everything else parses as a JSON array. As a safety net, content
 * that fails JSON parsing is retried as CSV before reporting an error — so a
 * CSV served from a non-`.csv` URL still renders.
 */
export function parseTableData(
  text: string,
  url: string,
): { rows: TableRow[]; parseError: string | null } {
  if (isCsvUrl(url)) {
    return { rows: csvToRows(text), parseError: null };
  }
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return { rows: parsed as TableRow[], parseError: null };
    return { rows: [], parseError: 'Expected JSON array' };
  } catch (err) {
    const csvRows = csvToRows(text);
    if (csvRows.length > 0) return { rows: csvRows, parseError: null };
    return {
      rows: [],
      parseError: err instanceof Error ? err.message : 'Invalid JSON',
    };
  }
}
